import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, AlertCircle } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABEL = {
    "kit-starter": { en: "AI Content Growth Kit · Starter", cn: "AI 内容增长配套 · Starter", price: "RM 88" },
    "kit-pro":     { en: "AI Content Growth Kit · Pro",     cn: "AI 内容增长配套 · Pro",     price: "RM 248" },
    "kit-premium": { en: "AI Content Growth Kit · Premium", cn: "AI 内容增长配套 · Premium", price: "RM 488" },
};

export default function KitSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [tx, setTx] = useState(null);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { lang } = useT();

    useEffect(() => {
        if (!sessionId) { setError(lang === "cn" ? "缺少 session_id" : "Missing session_id"); return; }
        let attempts = 0;
        const tick = async () => {
            attempts++;
            try {
                const { data } = await axios.get(`${API}/checkout/kit/status/${sessionId}`);
                setTx(data);
                if (data.payment_status === "paid") {
                    // Redirect to /welcome so they set a password before downloads.
                    navigate(`/welcome?session_id=${encodeURIComponent(sessionId)}&product=kit`, { replace: true });
                    return;
                }
                if (attempts >= 8) return;
                setTimeout(tick, 2500);
            } catch (e) {
                setError(e?.response?.data?.detail || (lang === "cn" ? "状态查询失败" : "Status check failed"));
            }
        };
        tick();
    }, [sessionId, lang, navigate]);

    const paid = tx?.payment_status === "paid";
    const tierMeta = tx?.tier ? TIER_LABEL[tx.tier] : null;
    const tierLabel = tierMeta ? (lang === "cn" ? tierMeta.cn : tierMeta.en) : "";

    return (
        <div className="min-h-screen bg-cafe-cream">
            <SiteNav />
            <main className="max-w-2xl mx-auto px-5 sm:px-8 pt-24 pb-20 text-center" data-testid="kit-success">
                {error ? (
                    <>
                        <AlertCircle className="h-12 w-12 text-cafe-terracotta mx-auto mb-4" />
                        <h1 className="font-display text-3xl text-cafe-espresso tracking-[-0.02em]">
                            {lang === "cn" ? "出错了" : "Something went wrong"}
                        </h1>
                        <p className="text-cafe-mocha mt-3">{error}</p>
                    </>
                ) : paid ? (
                    <>
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-cafe-olive/15 mb-5">
                            <Check className="h-7 w-7 text-cafe-olive" strokeWidth={2} />
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl text-cafe-espresso tracking-[-0.03em]">
                            {lang === "cn" ? "付款成功。" : "Payment received."}
                        </h1>
                        <p className="text-cafe-mocha mt-4 leading-relaxed">
                            {lang === "cn"
                                ? `感谢你购买 ${tierLabel}${tierMeta ? ` (${tierMeta.price})` : ""}。Kaily 会在 48 小时内联系你确认品牌问卷并启动交付。`
                                : `Thank you for purchasing ${tierLabel}${tierMeta ? ` (${tierMeta.price})` : ""}. Kaily will reach out within 48 hours to confirm your brand intake and begin delivery.`}
                        </p>
                        {tx?.email && (
                            <p className="text-cafe-mocha/70 text-sm mt-2">
                                {lang === "cn" ? "确认邮件已发送至 " : "Confirmation sent to "}<span className="text-cafe-espresso">{tx.email}</span>
                            </p>
                        )}
                        <div className="flex gap-3 justify-center mt-7 flex-wrap">
                            <button
                                onClick={() => navigate("/")}
                                className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream px-7 py-3 text-xs font-bold uppercase tracking-[0.14em]"
                                data-testid="kit-success-home"
                            >
                                {lang === "cn" ? "返回首页" : "Back to home"}
                            </button>
                            <button
                                onClick={() => navigate(lang === "cn" ? "/subscribe/cn" : "/subscribe")}
                                className="rounded-full border border-cafe-espresso/30 hover:border-cafe-espresso text-cafe-espresso px-7 py-3 text-xs font-bold uppercase tracking-[0.14em]"
                                data-testid="kit-success-upsell-cont"
                            >
                                {lang === "cn" ? "再添加月度订阅" : "Add monthly Continuity"}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="inline-block w-10 h-10 border-2 border-cafe-olive/30 border-t-cafe-olive rounded-full animate-spin mb-4"></div>
                        <h1 className="font-display text-3xl text-cafe-espresso tracking-[-0.02em]">
                            {lang === "cn" ? "正在确认你的付款…" : "Confirming your payment…"}
                        </h1>
                        <p className="text-cafe-mocha mt-3 text-sm">
                            {lang === "cn"
                                ? "通常只需几秒,请勿关闭页面。"
                                : "This usually takes a few seconds. Please don't close the page."}
                        </p>
                    </>
                )}
            </main>
            <SiteFooter />
        </div>
    );
}
